import * as z from "zod/v4";
import * as Option from "@gordonb/result/option";
import { NoteSchema } from "../note.ts";
import * as ForceCategory from "../force-category.ts";
import * as Arc from "../arc.ts";
import * as PaceLayer from "../pace-layer.ts";
import { ResultSchemaOf } from "../result.ts";
import { truncate } from "../../util/truncate.ts";
import {
  CoerceUnitIntervalSchema,
  UnitIntervalSchema,
} from "../unit-interval.ts";

export const ForceNoteMetaSchema = z.object({
  certainty: UnitIntervalSchema.default(0.5).describe(
    `The certainty of the driving force. This value should be between 0 and 1, where 0 means the consequences of the force are very uncertain, and 1 means the consequences of the force are completely certain.`,
  ),
  impact: UnitIntervalSchema.default(0.5).describe(
    `The impact of the driving force. This value should be between 0 and 1. 0 means low-impact, localized. 1 means extremely high world-scale impact.`,
  ),
  category: ForceCategory.ForceCategorySchema.optional(),
  arc: Arc.ArcSchema.optional(),
  pace: PaceLayer.PaceLayerSchema.optional(),
});

export const ForceNoteSchema = NoteSchema.extend({
  type: z.literal("force"),
  meta: ForceNoteMetaSchema,
});

export type ForceNote = z.infer<typeof ForceNoteSchema>;

const CoerceForceNoteMetaSchema = z.object({
  certainty: z.preprocess(Option.from, CoerceUnitIntervalSchema.default(0.5)),
  impact: z.preprocess(Option.from, CoerceUnitIntervalSchema.default(0.5)),
  category: z.preprocess(
    Option.from,
    ForceCategory.CoerceForceCategorySchema.optional(),
  ),
  arc: z.preprocess(Option.from, Arc.CoerceArcSchema.optional()),
  pace: z.preprocess(Option.from, PaceLayer.CoercePaceLayerSchema.optional()),
});

export const CoerceForceNoteSchema = ForceNoteSchema.extend({
  meta: CoerceForceNoteMetaSchema,
});

export const ForceNoteArraySchema = z.array(ForceNoteSchema);
export type ForceNoteArray = z.infer<typeof ForceNoteArraySchema>;

export const ForceNoteArrayResultSchema = ResultSchemaOf(
  ForceNoteArraySchema,
  z.string(),
);
export type ForceNoteArrayResult = z.infer<typeof ForceNoteArrayResultSchema>;

export const toUncertainty = (certainty: number): number => {
  return 1 - certainty;
};

/** An item version of a force note */
export type ForceNoteItem = {
  slug: string;
  title: string;
  summary: string;
  category: string | undefined;
  certainty: number;
  impact: number;
};

/** Convert a force note to an item version */
export const toForceNoteItem = (forceNote: ForceNote): ForceNoteItem => {
  return {
    slug: forceNote.slug,
    title: forceNote.title,
    summary: truncate(forceNote.content, 256),
    category: forceNote.meta.category,
    certainty: forceNote.meta.certainty,
    impact: forceNote.meta.impact,
  };
};
